import React, { useState } from "react";
import {
  AlertTriangle,
  Loader2,
  MoreVertical,
  Package,
  Pencil,
  ToggleLeft,
  ToggleRight,
  Trash2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/lib/format";

/**
 * Situação do estoque exibida no card.
 * `quantity` é o disponível (lotes: atual - reservado); `min_stock` o mínimo.
 */
export const getStockStatus = (product) => {
  const quantity = Number(product?.quantity || 0);
  const minStock = Number(product?.min_stock || 0);
  if (quantity <= 0) return { level: "out", quantity };
  if (minStock > 0 && quantity <= minStock) return { level: "low", quantity };
  return { level: "ok", quantity };
};

const STOCK_STYLES = {
  ok: "border-brand-pistachio/40 bg-brand-pistachio/15 text-brand",
  low: "border-terra/25 bg-terra-soft text-terra",
  out: "border-destructive/20 bg-destructive/10 text-destructive",
};

function StockPill({ product }) {
  const { level, quantity } = getStockStatus(product);
  const label =
    level === "out"
      ? "Esgotado (0 un.)"
      : level === "low"
      ? `Estoque baixo · ${quantity} un.`
      : `${quantity} un. em estoque`;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-semibold",
        STOCK_STYLES[level]
      )}
    >
      {level === "out" ? (
        <AlertTriangle className="h-3 w-3" />
      ) : (
        <span
          className={cn(
            "h-1.5 w-1.5 rounded-full",
            level === "low" ? "bg-terra" : "bg-brand"
          )}
        />
      )}
      {label}
    </span>
  );
}

/**
 * Card horizontal compacto do produto (docs/tela/code.html): foto com
 * categoria, nome + preço, descrição, estoque e ações rápidas
 * (pausar/ativar, editar e mais opções).
 */
export default function ProductListItem({
  product,
  categoryName,
  toggling = false,
  onToggle,
  onEdit,
  onDelete,
}) {
  // Guarda a URL que falhou: trocar a imagem na edição volta a exibi-la
  const [failedSrc, setFailedSrc] = useState(null);
  const paused = product.available === false;
  const isCombo = product.combo === "S";
  const showImage = product.image_url && failedSrc !== product.image_url;

  return (
    <article
      className={cn(
        "flex items-center gap-3 rounded-xl border bg-card p-2.5 shadow-soft transition-all hover:shadow-ambient",
        paused && "opacity-80 hover:opacity-100"
      )}
    >
      {/* Foto com categoria (ou selo de pausado) */}
      <div className="relative h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg bg-muted md:h-20 md:w-20">
        {showImage ? (
          <img
            src={product.image_url}
            alt={product.name}
            loading="lazy"
            onError={() => setFailedSrc(product.image_url)}
            className={cn("h-full w-full object-cover", paused && "grayscale")}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground">
            <Package className="h-6 w-6" />
          </div>
        )}
        {paused && (
          <span className="absolute left-1 top-1 rounded bg-foreground/90 px-1 text-[8px] font-bold uppercase tracking-wide text-background">
            Pausado
          </span>
        )}
        {categoryName && (
          <span
            className={cn(
              "absolute bottom-1 left-1 max-w-[calc(100%-0.5rem)] truncate rounded px-1 text-[9px] font-semibold",
              isCombo
                ? "bg-primary text-primary-foreground"
                : "border border-border/60 bg-card/90 text-muted-foreground backdrop-blur-sm"
            )}
          >
            {categoryName}
          </span>
        )}
      </div>

      {/* Nome, preço, descrição e estoque */}
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <h3
            className="truncate font-display text-sm font-bold leading-tight text-foreground"
            title={product.name}
          >
            {product.name}
          </h3>
          <span
            className={cn(
              "whitespace-nowrap font-display text-sm font-bold",
              paused ? "text-muted-foreground" : "text-primary"
            )}
          >
            {formatCurrency(product.price)}
          </span>
        </div>
        {product.description && (
          <p
            className="mt-0.5 truncate text-[11px] text-muted-foreground"
            title={product.description}
          >
            {product.description}
          </p>
        )}
        <div className="mt-1.5 flex items-center gap-2">
          <StockPill product={product} />
        </div>
      </div>

      {/* Ações rápidas */}
      <div className="flex flex-shrink-0 items-center gap-1.5 border-l border-border/60 pl-2">
        <button
          type="button"
          onClick={() => onToggle(product)}
          disabled={toggling}
          aria-pressed={!paused}
          aria-label={
            paused
              ? `Ativar ${product.name} no cardápio`
              : `Pausar ${product.name} no cardápio`
          }
          title={paused ? "Pausado no cardápio — ativar" : "Ativo no cardápio — pausar"}
          className={cn(
            "flex h-9 w-9 items-center justify-center rounded-lg transition-colors active:scale-95 disabled:cursor-wait disabled:opacity-60",
            paused
              ? "bg-surface text-muted-foreground hover:bg-muted hover:text-primary"
              : "bg-secondary text-primary hover:bg-secondary/70"
          )}
        >
          {toggling ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : paused ? (
            <ToggleLeft className="h-5 w-5" />
          ) : (
            <ToggleRight className="h-5 w-5" />
          )}
        </button>
        <button
          type="button"
          onClick={() => onEdit(product)}
          aria-label={`Editar ${product.name}`}
          title="Editar"
          className="flex h-9 w-9 items-center justify-center rounded-lg bg-surface text-foreground transition-colors hover:bg-muted active:scale-95"
        >
          <Pencil className="h-4 w-4" />
        </button>
        {/* modal={false}: o item abre um AlertDialog; com menu modal o Radix
            pode deixar o body travado (pointer-events: none) */}
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={`Mais opções de ${product.name}`}
              title="Mais opções"
              className="flex h-9 w-6 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <MoreVertical className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onSelect={() => onDelete(product)}
              className="text-destructive focus:bg-destructive/10 focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Excluir produto
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </article>
  );
}
